<script setup lang="ts">
import type { NoteConfig, Category, Layer } from "@/types/legend.types";
import { computed } from "vue";
import NoteCategoryItem from "./NoteCategoryItem.vue";
import NoteConfigItem from "./NoteConfigItem.vue";
import DragReorderList from "./DragReorderList.vue";
import type { InputInstance } from "element-plus";
import DRBtn from "@/components/UI/DRBtn.vue";
import UserState from "@/stores/UserState";
import { ref } from "vue";
import { v4 } from "uuid";
import { useToast } from "@/composable/useToast";

const { showDanger } = useToast();

const props = defineProps<{
  modelValue: NoteConfig[];
  layers: Layer[];
  categories: Category[];
}>();

const emits = defineEmits<{
  (e: "update:modelValue", layers: NoteConfig[]): void;
  (e: "update:categories", categories: Category[]): void;
  (e: "new-layer", value: { id: string; name: string }): void;
}>();

const notes = computed<NoteConfig[]>({
  get: () => props.modelValue,
  set: (value) => emits("update:modelValue", value),
});

const categories = computed<Category[]>({
  get: () => props.categories,
  set: (value) => emits("update:categories", value),
});

const defaultsNotes = computed<NoteConfig[]>({
  get: () => [...notes.value].filter((note) => note.type === "default"),
  set: (value: NoteConfig[]) => {
    notes.value = [
      ...notes.value.filter((note) => note.type !== "default"),
      ...value,
    ];
  },
});

const otherNotes = computed<NoteConfig[]>({
  get: () =>
    [...notes.value].filter(
      (note) => note.type !== "default" && note.type !== "photo"
    ),
  set: (value: NoteConfig[]) => {
    notes.value = [
      ...notes.value.filter(
        (note) => note.type === "default" || note.type === "photo"
      ),
      ...value,
    ];
  },
});

const addDefaultNote = ({
  note,
  index,
  categoryIds,
}: {
  note: NoteConfig;
  index?: number;
  categoryIds?: string[];
}) => {
  if (!index) {
    defaultsNotes.value = [...defaultsNotes.value, { ...note }];
  }
  if (index) {
    defaultsNotes.value = [
      ...defaultsNotes.value.slice(0, index),
      { ...note },
      ...defaultsNotes.value.slice(index),
    ];
  }
  if (categoryIds?.length && note.id) {
    const noteId = note.id;
    categories.value = categories.value.map((c) =>
      categoryIds.includes(c.id) ? { ...c, noteIds: [...c.noteIds, noteId] } : c
    );
  }
};

const updateDefaultNote = (value: NoteConfig) => {
  defaultsNotes.value = defaultsNotes.value.map((n) =>
    n.id === value.id ? value : n
  );
};

// Une note supprimée doit aussi disparaître des noteIds de tous les catalogues qui
// la référençaient.
const removeDefaultNote = (id: string) => {
  defaultsNotes.value = defaultsNotes.value.filter((n) => n.id !== id);
  categories.value = categories.value.map((c) => ({
    ...c,
    noteIds: c.noteIds.filter((noteId) => noteId !== id),
  }));
};

const removeNote = (id: string) => {
  otherNotes.value = otherNotes.value.filter((n) => n.id !== id);
};

// Change les catalogues auxquels une note appartient (depuis le multi-select de
// NoteConfigItem) : ajoute/retire son id des noteIds des catalogues concernés.
// Une note doit toujours appartenir à au moins un catalogue.
const updateNoteCategories = (noteId: string, categoryIds: string[]) => {
  if (categoryIds.length === 0) {
    showDanger("dashboard:alert.note.category.required");
    return;
  }
  categories.value = categories.value.map((c) => {
    const shouldInclude = categoryIds.includes(c.id);
    const currentlyIncludes = c.noteIds.includes(noteId);
    if (shouldInclude && !currentlyIncludes) {
      return { ...c, noteIds: [...c.noteIds, noteId] };
    }
    if (!shouldInclude && currentlyIncludes) {
      return { ...c, noteIds: c.noteIds.filter((id) => id !== noteId) };
    }
    return c;
  });
};

// Chaque catalogue possède directement l'ordre de ses notes (noteIds) : un
// réordonnancement au sein d'un catalogue ne touche donc qu'à CE catalogue, jamais
// aux autres — y compris pour une note appartenant à plusieurs catalogues.
const reorderCategoryNotes = (categoryId: string, reordered: NoteConfig[]) => {
  categories.value = categories.value.map((c) =>
    c.id === categoryId ? { ...c, noteIds: reordered.map((n) => n.id) } : c
  );
};

const newCategoryRef = ref<InputInstance | null>(null);
const newCategoryName = ref("");

const addCategory = () => {
  const categoryName = newCategoryName.value?.trim();
  if (!categoryName) return;
  if (categories.value.some((c) => c.name === categoryName)) return;
  categories.value = [...categories.value, { id: v4(), name: categoryName, noteIds: [] }];
  newCategoryName.value = "";
  newCategoryRef.value?.focus();
};

// Renommer/supprimer un catalogue ne touche plus qu'une seule entrée de `categories` :
// les notes référencent par id, donc rien à resynchroniser sur les note_configs.
const updateCategoryName = (id: string, newName: string) => {
  if (!newName) return;
  if (categories.value.some((c) => c.name === newName && c.id !== id)) return;
  categories.value = categories.value.map((c) =>
    c.id === id ? { ...c, name: newName } : c
  );
};

// Une note doit toujours appartenir à au moins un catalogue : on bloque la suppression
// si elle laisserait une note sans aucun autre catalogue plutôt que de la rendre
// silencieusement orpheline.
const deleteCategory = (id: string) => {
  const category = categories.value.find((c) => c.id === id);
  if (!category) return;
  const remaining = categories.value.filter((c) => c.id !== id);
  const wouldOrphanNotes = category.noteIds.some(
    (noteId) => !remaining.some((c) => c.noteIds.includes(noteId))
  );
  if (wouldOrphanNotes) {
    showDanger("dashboard:alert.category.has.orphan.notes");
    return;
  }
  categories.value = remaining;
};

// noteIds fait foi pour l'ordre : on résout chaque id vers son NoteConfig plutôt que
// de filtrer defaultsNotes (qui donnerait l'ordre du tableau global, pas celui du catalogue).
const notesForCategory = (category: Category): NoteConfig[] => {
  const byId = new Map(defaultsNotes.value.map((n) => [n.id, n]));
  return category.noteIds
    .map((id) => byId.get(id))
    .filter((n): n is NoteConfig => !!n);
};
</script>

<template>
  <div>
    <h5 class="mb-2">
      <el-icon class="me-3"><i class="bi-bricks"></i></el-icon>Catalogues des
      désordres
    </h5>

    <div class="mt-3 d-flex flex-column gap-4">
      <DragReorderList
        :items="categories"
        group="legend-categories"
        @reorder="categories = $event"
      >
        <template #default="{ item: category }">
          <NoteCategoryItem
            :category="category"
            :notes="notesForCategory(category)"
            :layers="layers"
            :categories="categories"
            @new-layer="$emit('new-layer', $event)"
            @remove="removeDefaultNote"
            @update-note="updateDefaultNote"
            @update-note-categories="updateNoteCategories"
            @new-note="addDefaultNote"
            @rename="updateCategoryName(category.id, $event)"
            @delete-category="deleteCategory(category.id)"
            @reorder-notes="reorderCategoryNotes(category.id, $event)"
          />
        </template>
      </DragReorderList>
    </div>
    <div class="d-flex gap-3 pt-4">
      <el-input
        ref="newCategoryRef"
        class="col-8 col-md-6 col-lg-4"
        size="small"
        v-model="newCategoryName"
        :placeholder="$t('dashboard:new.catalogue')"
        @keyup.enter="addCategory()"
      />
      <DRBtn
        class="col-auto"
        variant="secondary"
        :label="$t('add')"
        @click="addCategory()"
      />
    </div>

    <!-- TODO: remove if all legends are fixed -->
    <div
      v-if="UserState.isSuperAdmin() && otherNotes.length > 0"
      class="mt-5 p-3 bg-warning text-white rounded"
    >
      <span class=""
        >superadmin : peut uniquement supprimer les notes suivantes
      </span>
      <NoteConfigItem
        v-for="note in otherNotes"
        :key="note.id"
        :model-value="note"
        :layers="layers"
        :categories="categories"
        type="photo"
        @remove="removeNote(note.id)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
